import TableOnly from "components/DataTable/TableOnly";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";
import { truncateString } from "utils/UtilityFuncs";

export default function EmailApprovals({ selectedCustomer, height }) {
  const dispatch = useDispatch();
  /*   ALL STATES
   ********************************************* */
  const [filterBy, setFilterBy] = useState({
    name: "Filter by All",
    value: null,
  });
  const [emailApproval, setEmailApproval] = useState([]);

  const [loading, setLoading] = useState(false);

  // Pagination/Search states
  const [pageLimit, setPageLimit] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  /*   All Functions
   ********************************************* */
  const getEmailApproval = useCallback(
    async (opts = {}) => {
      try {
        if (!selectedCustomer) {
          // reset data
          setEmailApproval([]);
          setTotal(0);
          setTotalPages(0);
          return;
        }
        setLoading(true);
        const pageToUse = opts.page || pageNumber;
        const limitToUse = opts.limit || pageLimit;
        const queryToUse =
          opts.query !== undefined ? opts.query : appliedSearch;

        const { data } = await API.post(
          "/emails/crm-approvals?page=" + pageToUse + "&limit=" + limitToUse,
          {
            customerId: selectedCustomer.customerId,
            query: queryToUse,
            filterBy:
              queryToUse && queryToUse !== "" ? filterBy.value : undefined,
          },
          { headers: authHeader() }
        );

        if (data?.success) {
          // API returns { success, data, total, page, limit, totalPages }
          // On page 1, replace data; otherwise append
          if (pageToUse === 1) {
            setEmailApproval(data?.data || []);
          } else {
            setEmailApproval((prev) => [...prev, ...(data?.data || [])]);
          }

          setTotal(data?.total || 0);
          setTotalPages(data?.pages || data?.totalPages || 0);
          setPageNumber(data?.page || pageToUse);
          setPageLimit(data?.limit || limitToUse);

          if (opts.query !== undefined) {
            setAppliedSearch(queryToUse);
          }
        } else {
          dispatch(
            setNotification({
              message: data?.message || "Failed to fetch",
              type: "error",
            })
          );
        }
      } catch (err) {
        dispatch(setNotification({ message: err.message, type: "error" }));
      } finally {
        setLoading(false);
      }
    },
    [
      pageNumber,
      selectedCustomer,
      pageLimit,
      appliedSearch,
      filterBy.value,
      dispatch,
    ]
  );

  // Load more data
  const handleLoadMore = () => {
    if (pageNumber < totalPages) {
      getEmailApproval({ page: pageNumber + 1 });
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPageNumber(1);
    if (searchTerm && searchTerm !== "") {
      getEmailApproval({ query: searchTerm, page: 1 });
    } else {
      getEmailApproval({ query: "", page: 1 });
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setAppliedSearch("");
    setPageNumber(1);
    getEmailApproval({ query: "", page: 1 });
  };

  const handleLimitChange = (newLimit) => {
    setPageLimit(newLimit);
    setPageNumber(1);
    getEmailApproval({ page: 1, limit: newLimit });
  };

  useEffect(() => {
    let timeout = setTimeout(() => {
      if (selectedCustomer) {
        setPageNumber(1);
        getEmailApproval({ page: 1 });
      } else {
        // reset data
        setEmailApproval([]);
        setTotal(0);
        setTotalPages(0);
      }
    }, 50);
    return () => clearTimeout(timeout);
  }, [pageLimit, filterBy, selectedCustomer]);

  // Debounced search
  useEffect(() => {
    let timeout = setTimeout(() => {
      if (searchTerm !== appliedSearch && selectedCustomer) {
        setPageNumber(1);
        getEmailApproval({ page: 1, query: searchTerm });
      }
    }, 500);
    return () => clearTimeout(timeout);
  }, [searchTerm, selectedCustomer]);

  /*   Table functions
   ********************************************* */

  const table_head = [
    { name: "WA-ID", value: "approvalId" },
    { name: "W-ID", value: "woId" },
    { name: "Job Name", value: "jobName" },
    // { name: "Sub Job", value: "subJobName" },
    // { name: "W-Status", value: "WOStatus" },
    { name: "Approved", value: "allApproveds" },

    // { name: "Contact Name", value: "contactName" },
    // { name: "Contact Number", value: "contactNumber" },
    { name: "Actions", value: "Actions" },
  ];

  const table_body = (item, index) => (
    <tr className="" key={item.id}>
      <td className="align-middle text-nowrap py-1">WA-{item.approvalId}</td>
      <td className="align-middle text-nowrap py-1">W-{item.woId}</td>
      <td className="align-middle py-1 w-100">
        {truncateString(item?.WorkOrder?.jobName, 35)}
      </td>
      {/* <td className="align-middle py-1">{item?.WorkOrder?.subJobName}</td>
      <td className="align-middle py-1">{item?.WorkOrder?.WOStatus}</td> */}
      <td className="align-middle py-1">{item.allApproved ? "Yes" : "No"}</td>
      <td className="align-middle py-1 text-end table-action">
        <Link
          to={`/work-order/details/${item?.WorkOrder?.WOStatus}/${item?.WorkOrder?.woId}?page=1`}
          className="btn btn-primary btn-sm py-0 px-1"
        >
          View
        </Link>
      </td>
    </tr>
  );

  return (
    <div className="">
      {loading && <Spinner />}
      {/*   Table
       ********************************************* */}
      <div className="d-flex justify-content-between mb-0 align-items-center">
        <div className="ps-2 d-flex align-items-center justify-content-between w-75 gap-2">
          <div>
            <h4 className="text-decoration-underline">Approvals</h4>
          </div>
          <div className="d-flex gap-2 align-items-center me-2">
            <div className="btn-group">
              <button
                className="px-1 bg-primary bg-opacity-10 border border-primary rounded-end rounded-start "
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                {pageLimit}
              </button>
              <ul className="dropdown-menu w-25">
                <li onClick={() => handleLimitChange(3)}>
                  <button className="dropdown-item">3</button>
                </li>
                <li onClick={() => handleLimitChange(5)}>
                  <button className="dropdown-item">5</button>
                </li>
                <li onClick={() => handleLimitChange(10)}>
                  <button className="dropdown-item">10</button>
                </li>
                <li onClick={() => handleLimitChange(20)}>
                  <button className="dropdown-item">20</button>
                </li>
                <li onClick={() => handleLimitChange(50)}>
                  <button className="dropdown-item">50</button>
                </li>
              </ul>
            </div>
            <span className="text-muted">
              Page: {pageNumber} / {totalPages}
            </span>
            <span className="text-muted">Total: {total}</span>
          </div>
        </div>

        <div className="d-flex">
          {/* {buttons()} */}
          <form
            onSubmit={handleSearch}
            className="d-flex justify-content-end gap-1 align-items-center"
          >
            <input
              type="search"
              placeholder="Search here..."
              value={searchTerm}
              className="form-control form-control-sm"
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </form>
        </div>
      </div>

      <div
        className="px-1 flex-grow-1 d-flex flex-column"
        style={{
          height: height ? height : "50vh",
          overflowY: "auto",
          borderRadius: 15,
        }}
      >
        <div className="horizontal-scrollable d-flex flex-grow-1">
          <TableOnly
            table_head={table_head}
            table_body={table_body}
            table_data={emailApproval}
            disableSorting={true}
            actionsOnEnd={true}
            tableSize="sm"
          />
        </div>

        {/* Load More Button */}
        {pageNumber < totalPages && (
          <div
            className="p-2 text-center bg-light mt-2"
            style={{ borderRadius: 10 }}
          >
            <button
              type="button"
              className="btn btn-outline-primary btn-sm px-4"
              onClick={handleLoadMore}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Loading...
                </>
              ) : (
                <>
                  <i className="mdi mdi-chevron-down me-1"></i>
                  Load More
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
